import _ from "lodash"
import { createSlice } from "@reduxjs/toolkit"

import { SandboxSchemaState } from "./ScheduleSchema"
import { validateStateInDev } from "./validateStateInDev"
import { deallocatePatient, transferPatientsBetweenNursesAndChairs } from "./atomicActions"

const sandboxSlice = createSlice({
	name: "sandbox",
	initialState: {
		isLocked: false,
		triedToLock: false,
		acuityMix: {},
		chairSchedule: [],
		nurseSchedule: [],
		utilizationCurve: [],
		chargeNurse: null,
		patientSchedule: [],
		isSandboxMode: false,
		openTime: "08:00",
		closeTime: "20:00",
		status: "idle", // Track loading states: 'idle', 'loading', 'succeeded', 'failed'
		error: null, // Store error messages if any
		documentId: null // Add _id to the state
	},
	reducers: {
		//ATOMIC ACTIONS
		deallocatePatientSandbox: deallocatePatient(SandboxSchemaState),

		transferPatientsBetweenNursesAndChairsSandbox: transferPatientsBetweenNursesAndChairs(SandboxSchemaState),

		//NON-ATOMIC ACTIONS
		setSandboxOptimizeLoading: state => {
			state.status = "loading"
			state.error = null // Clear any previous error
		},
		setSandboxOptimizeData: (state, action) => {
			const newState = _.cloneDeep(state)

			newState.status = "succeeded"
			const payload = action.payload

			newState.isLocked = payload.locked
			newState.acuityMix = payload.acuityMix
			newState.chairSchedule = payload.chairSchedule
			newState.nurseSchedule = payload.nurseSchedule
			newState.utilizationCurve = payload.utilizationCurve
			newState.patientSchedule = payload.patientSchedule
			newState.chargeNurse = payload.chargeNurse
			newState.openTime = payload.openTime
			newState.closeTime = payload.closeTime
			newState.documentId = payload._id
			newState.error = null // Clear any previous error on success

			const validatedNewState = validateStateInDev(SandboxSchemaState, newState)

			return validatedNewState
		},
		setSandboxMode: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.isSandboxMode = action.payload

			const validatedNewState = validateStateInDev(SandboxSchemaState, newState)
			return validatedNewState
		},
		setSandboxOptimizeError: (state, action) => {
			state.status = "failed"
			state.error = action.payload // Store the error message
		},
		updateSandboxSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const { patientSchedule, nurseSchedule, chairSchedule } = action.payload

			const newState = _.cloneDeep(state)

			if (patientSchedule) {
				newState.patientSchedule = patientSchedule
			}
			if (nurseSchedule) {
				newState.nurseSchedule = nurseSchedule
			}
			if (chairSchedule) {
				newState.chairSchedule = chairSchedule
			}

			const validatedNewState = validateStateInDev(SandboxSchemaState, newState)

			return validatedNewState
		},
		setSandboxChairSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.chairSchedule = action.payload
			const validatedNewState = validateStateInDev(SandboxSchemaState, newState)
			return validatedNewState
		},
		setSandboxNurseSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)
			newState.nurseSchedule = action.payload
			const validatedNewState = validateStateInDev(SandboxSchemaState, newState)
			return validatedNewState
		},
		removeSandboxNurse: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			const nurseId = action.payload

			newState.nurseSchedule = newState.nurseSchedule.filter(
				nurse => nurse.nurseId !== nurseId
			)
			newState.patientSchedule = newState.patientSchedule.map(patient =>
				patient.assignedNurse === nurseId ? { ...patient, assignedNurse: null } : patient
			)

			const validatedNewState = validateStateInDev(SandboxSchemaState, newState)

			return validatedNewState
		},
		updateSandboxPatientInSchedule: (state, action) => {
			if (state.status !== "succeeded") {
				return state
			}

			const newState = _.cloneDeep(state)

			const { updatedPatient } = action.payload

			// Update patientSchedule
			newState.patientSchedule = newState.patientSchedule.map(patient =>
				patient.patientId === updatedPatient.patientId ? updatedPatient : patient
			)

			const validatedNewState = validateStateInDev(SandboxSchemaState, newState)

			return validatedNewState
		}
	}
})

export const {
	deallocatePatientSandbox,
	transferPatientsBetweenNursesAndChairsSandbox,
	setSandboxOptimizeLoading,
	setSandboxOptimizeData,
	setSandboxMode,
	setSandboxOptimizeError,
	updateSandboxSchedule,
	setSandboxChairSchedule,
	setSandboxNurseSchedule,
	removeSandboxNurse,
	updateSandboxPatientInSchedule
} = sandboxSlice.actions

export default sandboxSlice.reducer
